import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import {
  fetchMySavedSearches,
  deleteSavedSearchApi,
  updateSavedSearchApi,
} from "../../actions/savedSearch";
import LinkedText from "../common/LinkedText";

const MyLists = ({ token }) => {
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [editDescription, setEditDescription] = useState("");

  const loadLists = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const data = await fetchMySavedSearches(token);
      setLists(data);
      setError(null);
    } catch (err) {
      setError("Failed to load your lists");
    } finally {
      setLoading(false);
    }
  };

  useEffect(function loadListsWhenTokenChanges() {
    loadLists();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this list?")) return;
    try {
      await deleteSavedSearchApi(id, token);
      setLists(lists.filter((l) => l._id !== id));
    } catch {
      alert("Failed to delete list");
    }
  };

  const handleToggleVisibility = async (list) => {
    const newVisibility =
      list.visibility === "public" ? "private" : "public";
    try {
      const updated = await updateSavedSearchApi(
        list._id,
        { visibility: newVisibility },
        token
      );
      setLists(lists.map((l) => (l._id === list._id ? updated : l)));
    } catch {
      alert("Failed to update visibility");
    }
  };

  const startEdit = (list) => {
    setEditingId(list._id);
    setEditName(list.name);
    setEditDescription(list.description || "");
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditName("");
    setEditDescription("");
  };

  const saveEdit = async () => {
    try {
      const updated = await updateSavedSearchApi(
        editingId,
        { name: editName, description: editDescription },
        token
      );
      setLists(lists.map((l) => (l._id === editingId ? updated : l)));
      cancelEdit();
    } catch {
      alert("Failed to update list");
    }
  };

  if (loading) {
    return (
      <div style={{ padding: "24px", width: "100%" }}>
        <p>Loading your lists...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: "24px", width: "100%" }}>
        <p style={{ color: "red" }}>{error}</p>
      </div>
    );
  }

  return (
    <div style={{ padding: "24px", width: "100%" }}>
      <h3>My Lists</h3>
      {lists.length === 0 ? (
        <div style={{ textAlign: "center", padding: "40px 20px", color: "#666" }}>
          <i
            className="fas fa-bookmark"
            style={{ fontSize: "48px", marginBottom: "16px", opacity: 0.3 }}
          />
          <p>You haven't saved any searches yet.</p>
          <p>
            Search for restaurants and click <strong>"Save"</strong> to get
            started.
          </p>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          {lists.map((list) => (
            <div
              key={list._id}
              style={{
                border: "1px solid #ddd",
                borderRadius: "8px",
                padding: "16px",
                background: "#fff",
              }}
            >
              {editingId === list._id ? (
                <div>
                  <input
                    type="text"
                    className="text-form"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    style={{ marginBottom: "8px", width: "100%" }}
                  />
                  <textarea
                    className="text-form"
                    value={editDescription}
                    onChange={(e) => setEditDescription(e.target.value)}
                    placeholder="Description (optional)"
                    rows={2}
                    style={{
                      marginBottom: "8px",
                      width: "100%",
                      resize: "vertical",
                    }}
                  />
                  <div style={{ display: "flex", gap: "8px" }}>
                    <button className="btn-primary" onClick={saveEdit}>
                      Save
                    </button>
                    <button className="btn-secondary" onClick={cancelEdit}>
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                    }}
                  >
                    <div>
                      <Link
                        to={`/lists/${list.slug}`}
                        style={{ fontWeight: 600, fontSize: "1.1em" }}
                      >
                        {list.name}
                      </Link>
                      {list.description && (
                        <p
                          style={{
                            margin: "4px 0 0",
                            fontSize: "0.9em",
                            color: "#666",
                          }}
                        >
                          <LinkedText text={list.description} />
                        </p>
                      )}
                      <div
                        style={{
                          display: "flex",
                          gap: "12px",
                          marginTop: "8px",
                          fontSize: "0.85em",
                          color: "#888",
                        }}
                      >
                        {list.cityName && (
                          <span>
                            <i className="fas fa-map-marker-alt" />{" "}
                            {list.cityName}
                            {list.state ? `, ${list.state}` : ""}
                          </span>
                        )}
                        <span
                          style={{
                            background:
                              list.visibility === "public"
                                ? "#e8f5e9"
                                : "#f5f5f5",
                            padding: "2px 8px",
                            borderRadius: "4px",
                            fontSize: "0.85em",
                          }}
                        >
                          {list.visibility === "public" ? "Public" : "Private"}
                        </span>
                        {list.notifications?.enabled && (
                          <span>
                            <i className="fas fa-bell" /> Notifications on
                          </span>
                        )}
                        <span>
                          {new Date(list.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: "8px" }}>
                      <button
                        type="button"
                        onClick={() => startEdit(list)}
                        title="Edit"
                        style={{
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          color: "#666",
                        }}
                      >
                        <i className="fas fa-edit" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleToggleVisibility(list)}
                        title={
                          list.visibility === "public"
                            ? "Make private"
                            : "Make public"
                        }
                        style={{
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          color: "#666",
                        }}
                      >
                        <i
                          className={`fas ${
                            list.visibility === "public"
                              ? "fa-eye"
                              : "fa-eye-slash"
                          }`}
                        />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(list._id)}
                        title="Delete"
                        style={{
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          color: "#c62828",
                        }}
                      >
                        <i className="fas fa-trash" />
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const mapStateToProps = (state) => ({
  token: state.auth.token,
});

export default connect(mapStateToProps)(MyLists);
